/** @format */

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import LabelInput from "../../../shared/components/labelInput";
import useQuoteStore from "../store/QuoteStore";
import useAppStateStore from "../../../shared/store/AppStateStore";
import Tq6Mapper from "../mappers/tq6Mapper";
import QuoteFormWrapper from "../../../shared/components/quoteFormWrapper";
import FormNavigationButtons from "../../../shared/components/FormNavigationButtons";
import QuoteStep from "../enums/QuoteStep";
import { handleNext } from "../../../shared/utils/navigationUtils";
import { useNavigate } from "react-router-dom";
import {
  saveQuote,
  fetchPanelData,
  fetchPolicyDetails,
} from "../services/quoteService";
import {
  fetchOverNight,
  fetchKeeper,
  fetchOwner,
} from "../../../shared/services/simpleListItemsService";
import {
  downloadPrivacyPolicy,
  downloadTOBA,
} from "../../../shared/utils/apiUtils";
import { HotjarEventNames } from "../../../shared/utils/hotjarEventNames";
import { TrackHotjarEvent } from "../../../shared/utils/trackHotjarEvent";
import RadioGroup from "../../../shared/components/RadioGroup";

const Tq6 = () => {
  // Store
  const navigate = useNavigate();
  // Form Setup
  const {
    register,
    formState: { errors, isValid },
    watch,
    control,
    trigger,
    handleSubmit,
  } = useForm({
    defaultValues: Tq6Mapper.get(),
  });

  const STATE = useAppStateStore();
  // Use flat state keys as per AppStateStore

  // State
  const [overNight, setOverNight] = useState([]);
  const [keeper, setKeeper] = useState([]);
  const [owner, setOwner] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const convertArrayFormat = (array) => {
    if (!Array.isArray(array)) {
      return [];
    }
    return array.map((item) => ({
      value: item.listItemId,
      label: item.listItemDescription,
    }));
  };

  // Effects
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [overNightData, keeperData, ownerData] = await Promise.all([
          fetchOverNight(),
          fetchKeeper(),
          fetchOwner(),
        ]);

        setOverNight(overNightData);
        setKeeper(keeperData);
        setOwner(ownerData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQ6_ENTERED);
  }, []);

  // Event Handlers
  const onSubmit = async (data) => {
    await new Promise((resolve) => {
      Tq6Mapper.set(data);
      setTimeout(resolve, 100);
    });
    if (isValid) {
      TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQ6_NEXT_CLICKED);
    }
    const latestClaims = useAppStateStore.getState().claims;
    const latestAdditionalDriver = useAppStateStore.getState().additionalDriver;
    if (latestClaims === "Yes") {
      handleNext(QuoteStep.TQ_CLAIM);
    } else if (latestAdditionalDriver === "Yes") {
      handleNext(QuoteStep.TQ_ADD_DRIVER);
    } else {
      const quoteData = useQuoteStore.getState().quote;
      saveQuote(quoteData, navigate).then((data) => {
        fetchPolicyDetails().then(() => {
          fetchPanelData(null, data.risk.riskObjectId).then(() => {
            handleNext(QuoteStep.TQ_PANEL);
          });
        });
      });
      handleNext(QuoteStep.TQ_PANEL_LOADER);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <QuoteFormWrapper>
          <h1 className="margin-bottom-10">Quote Details</h1>
          <p className="margin-bottom-30">
            We're almost there—just a few more details to complete your quote.
          </p>

          <div className="fields">
            <>
              <RadioGroup
                name="RegOwner"
                mainLabel="Are you the registered owner of your vehicle?"
                register={register}
                errors={errors.RegOwner}
              />
              {watch("RegOwner") === "No" && (
                <LabelInput
                  id="owner"
                  label="Who is the registered owner of your vehicle?"
                  type="select"
                  control={control}
                  options={convertArrayFormat(owner)}
                  placeholder="Enter registered owner "
                  register={register("owner", {
                    required: "Required",
                  })}
                  errors={errors.owner}
                  watch={watch("owner")}
                  trigger={trigger}
                  isLoading={isLoading}
                />
              )}
              <RadioGroup
                name="Regkeeper"
                mainLabel="Are you the registered keeper of your vehicle?"
                register={register}
                errors={errors.Regkeeper}
              />

              {watch("Regkeeper") === "No" && (
                <LabelInput
                  id="keeper"
                  label="Who is the registered keeper of your vehicle?"
                  type="select"
                  control={control}
                  options={convertArrayFormat(keeper)}
                  placeholder="Enter registered keeper "
                  register={register("keeper", {
                    required: "Required",
                  })}
                  errors={errors.keeper}
                  watch={watch("keeper")}
                  trigger={trigger}
                  isLoading={isLoading}
                />
              )}
              <LabelInput
                id="marketValue"
                label="What is the estimated market value of your vehicle?"
                type="number"
                placeholder="e.g 7,000"
                register={register("marketValue", {
                  required: "Required",
                  maxLength: {
                    value: 6,
                    message: "Price must be a number with at most 6 digits",
                  },
                  pattern: {
                    value: /^[0-9]{1,6}$/,
                    message: "Price must be a number with at most 6 digits",
                  },
                })}
                errors={errors.marketValue}
                // pattern="[0-9]{6}"
                watch={watch("marketValue")}
                trigger={trigger}
                maxLength={6}
                onInput={(e) => {
                  // Enforce numeric input and restrict to 6 digits
                  e.target.value = e.target.value.slice(0, 6);
                }}
              />
              <RadioGroup
                name="overNightHome"
                mainLabel="Will the vehicle be parked on your drive overnight?"
                register={register}
                errors={errors.overNightHome}
              />
              {watch("overNightHome") === "No" && (
                <>
                  <LabelInput
                    id="locOverNight"
                    label="Where is your vehicle located overnight?"
                    type="select"
                    options={convertArrayFormat(overNight)}
                    control={control}
                    placeholder="Select location"
                    register={register("locOverNight", { required: true })}
                    errors={errors.locOverNight}
                    watch={watch("locOverNight")}
                    trigger={trigger}
                    isSearchable={true}
                    isLoading={isLoading}
                  />

                  <LabelInput
                    id="overNightPostcode"
                    label="What is the overnight postcode?"
                    type="text"
                    value="46000"
                    control={control}
                    placeholder="Overnight postcode"
                    register={register("overNightPostcode", { required: true })}
                    errors={errors.overNightPostcode}
                    watch={watch("overNightPostcode")}
                    trigger={trigger}
                    isSearchable={true}
                  />
                </>
              )}
              <RadioGroup
                name="AdditionalClaims5Years"
                mainLabel="Have you had any accidents, losses, thefts, fire, flood or
                claims in any vehicle regardless of fault or whether you claimed
                or not in the last 5 years?"
                register={register}
                errors={errors.AdditionalClaims5Years}
              />

              <div className="termsContainer">
                <input
                  type="checkbox"
                  id="termsAccepted"
                  {...register("termsAccepted", {
                    required:
                      "You cannot obtain a quote without confirming acceptance our Terms of Business and Privacy Policy",
                  })}
                />
                <label htmlFor="termsAccepted" className="termsLabel">
                  I confirm that I have read and understood the{" "}
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      downloadTOBA({ navigate });
                    }}
                    className="transparentButton"
                  >
                    Terms of Business
                  </a>{" "}
                  and{" "}
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      downloadPrivacyPolicy({ navigate });
                    }}
                    className="transparentButton"
                  >
                    Privacy Policy
                  </a>
                  .
                </label>

                {errors.termsAccepted && (
                  <span className="errorField font-12">
                    {errors.termsAccepted.message}
                  </span>
                )}
              </div>
            </>
          </div>
        </QuoteFormWrapper>
        <FormNavigationButtons prevStep={QuoteStep.TQ5} isValid={isValid} />
      </form>
    </>
  );
};

export default Tq6;
